import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettingsStore, type TextScale } from '@/lib/settings-store';
import styles from './BibleFontSizePicker.module.css';

/**
 * Reader header font-size picker (device-feedback Part 4). A 44x44 "Aa" icon
 * button, sibling to BibleThemePicker, that switches the app-wide text-size
 * preference (settings-store.textScale: default | large | xl) without leaving
 * the chapter. setTextScale applies html[data-text-scale] live, so the reader
 * reflows immediately and the choice persists (it is the same preference the
 * Settings TextSizeToggle drives).
 *
 * Same lightweight anchored-menu pattern as BibleThemePicker (document-level
 * click-outside + Escape while open; NOT a NavStack layer — rule #78 governs
 * full-screen overlays, not anchored menus). Chrome px-fixed (rule #15); the
 * trigger clears the 44px touch floor (rule #16). The popover reuses the
 * Settings text-size labels (settings.textSize*) so the two surfaces stay in
 * sync.
 */
export default function BibleFontSizePicker() {
  const { t } = useTranslation();
  const textScale = useSettingsStore((s) => s.textScale);
  const setTextScale = useSettingsStore((s) => s.setTextScale);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Document-level click-outside + Escape, attached only while open.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const options: Array<{ value: TextScale; labelKey: string }> = [
    { value: 'default', labelKey: 'settings.textSizeDefault' },
    { value: 'large', labelKey: 'settings.textSizeLarge' },
    { value: 'xl', labelKey: 'settings.textSizeXL' },
  ];

  const handleSelect = (value: TextScale) => {
    setTextScale(value);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('bible.textSizeLabel')}
        onClick={() => setOpen((v) => !v)}
      >
        {/* "Aa" text-size glyph: a large A + small a, currentColor so it
            follows the theme (mirrors the theme picker's icon register). */}
        <span className={styles.glyph} aria-hidden="true">
          <span className={styles.glyphLarge}>A</span>
          <span className={styles.glyphSmall}>a</span>
        </span>
      </button>
      {open && (
        <div
          className={styles.popover}
          role="radiogroup"
          aria-label={t('bible.textSizeLabel')}
        >
          {options.map((opt) => {
            const active = textScale === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={`${styles.option} ${active ? styles.optionActive : ''}`.trim()}
                onClick={() => handleSelect(opt.value)}
              >
                <span className={styles.optionLabel}>{t(opt.labelKey)}</span>
                {active && (
                  <svg
                    className={styles.check}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
